import React, { Fragment } from "react";
import { Column, Grid } from "../../../../components/src";
import { gql, useQuery } from "urql";
import { groupBy } from "lodash";
import { numberFormatFun } from "../../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../../components/src/dateForm";
import { useQueryState } from "../../../../components/src/use-query-state";

const GetFunds = gql`
    query funds($startDate: GraphQLDateString) {
        parties(filter: { typeIn: [Fund], statusIn: [Confirmed] }) {
            _id
            name
        }
        transactionItems(filter: { itemTypeIn: [ManagementCost], startDate: $startDate }) {
            clientId
            amount
            valueDate
        }
    }
`;

const moneyFormat = numberFormatFun("# ##0.00");

const getDefaultStartDate = (): string => {
    const startDate = new Date();
    // Set to first day of current month, then go back 2 months to show 3 months total
    startDate.setDate(1);
    startDate.setMonth(startDate.getMonth() - 2);
    return startDate.toISOString().slice(0, 10);
};

export const MonthlyCostsPage = (): React.ReactElement => {
    const [startDate] = useQueryState("startDate", getDefaultStartDate());

    const [{ fetching, error, data }] = useQuery({
        query: GetFunds,
        variables: { startDate },
        requestPolicy: "cache-and-network"
    });

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    // Group by month (YYYY-MM)
    const itemsByMonth = groupBy(data.transactionItems, (i) => i.valueDate.slice(0, 7));
    // Compute latest date per month
    const latestDateByMonth: Record<string, string> = Object.fromEntries(
        Object.entries(itemsByMonth).map(([month, items]) => [month, items.map((x) => x.valueDate).sort((a, b) => (a > b ? -1 : 1))[0]])
    );

    // Columns are the latest date for each month
    const columns: string[] = Object.values(latestDateByMonth).sort((a, b) => (a > b ? -1 : a < b ? 1 : 0));

    const gridColumns = [
        { name: "name", title: "Fund", className: "" },
        ...columns.map((c) => {
            return { name: c, title: c, className: "right" };
        })
    ];

    const gridData = data.parties.map((fund) => {
        const item = {
            name: fund.name
        };
        columns.forEach((column) => {
            const monthKey = column.slice(0, 7);
            const monthItems = itemsByMonth[monthKey] ?? [];
            const sum = monthItems
                .filter((x) => x.clientId == fund._id && x.valueDate <= column)
                .reduce((acc, currentValue) => acc + currentValue.amount, 0);
            item[column] = moneyFormat(sum);
        });
        return item;
    });

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-6/12 px-2 flex mt-3">
                    <DateForm defaultDateString={startDate} dateName={"startDate"}></DateForm>
                </div>
            </div>
            <Grid header="Costs - Monthly" data={gridData} sortable tableClassName="table-xs">
                {gridColumns.map((col, i) => (
                    <Column className={`nowrap ${col.className}`} key={i} field={col.name} title={col.title} />
                ))}
            </Grid>
        </Fragment>
    );
};
